import dayjs from "@/lib/day";
import formStyles from "@/styles/Form.module.scss";
import paymentMethodStyles from "@/styles/PaymentMethod.module.scss";
import classnames from "classnames";
import merge from "deepmerge";
import { forwardRef, useCallback, useEffect, useReducer, useRef } from "react";
import SelectField from "./SelectField";
import ErrorMessage from "./ErrorMessage";

function createOption(value, format) {
  const valueWithZero = value < 10 ? "0" + value : String(value);
  return { value: valueWithZero, label: `${value}${format}` };
}

function yearOptions() {
  const now = dayjs();
  const year = now.year();
  const maxYear = year + 15;
  const options = [];
  for (let i of Array(maxYear - year + 1).keys()) {
    const value = year + i;
    options.push(createOption(value, "年"));
  }
  return options;
}

function monthOptions() {
  const options = [];
  for (let i of Array(12).keys()) {
    const value = i + 1;
    options.push(createOption(value, "月"));
  }
  return options;
}

const initialState = {
  expirationDate: "",
  year: "",
  month: "",
};

const ExpirationDateField = forwardRef((props, ref) => {
  const {
    error,
    className,
    name,
    title,
    caption,
    required,
    setValue,
    onChange: rfOnChange,
    onBlur: rhfOnBlur,
    ..._props
  } = props;

  const isMounted = useRef(false);

  const customStylesMonth = {
    container: (provided) => ({
      ...provided,
      width: "5em",
      minWidth: "5em",
    }),
    singleValue: (provided) => ({
      ...provided,
      maxWidth: "100%",
      overflow: "visible",
      whiteSpace: "nowrap",
      display: "inline-flex",
      alignItems: "center",
      padding: 0,
      margin: 0,
    }),

    input: (provided) => ({
      ...provided,
      margin: "0",
      paddingBottom: "0px",
      paddingTop: "0px",
      color: "#666",
      width: "100%",
    }),
  };

  const customStylesYear = {
    container: (provided) => ({
      ...provided,
      width: "6.8em",
    }),
  };

  const reducer = useCallback((state, action) => {
    const newState = { ...state };
    switch (action.type) {
      case "year":
        return merge(newState, {
          year: action.value,
          expirationDate: `${action.value}${state.month}`,
        });
      case "month":
        return merge(newState, {
          month: action.value,
          expirationDate: `${state.year}${action.value}`,
        });
      default:
        return state;
    }
  }, []);
  const [state, dispatch] = useReducer(reducer, initialState);

  const handleYearChange = useCallback((option) => {
    dispatch({ type: "year", value: option.value });
  }, []);

  const handleMonthChange = useCallback((option) => {
    dispatch({ type: "month", value: option.value });
  }, []);

  const handleBlur = useCallback(() => {
    if (rhfOnBlur) {
      rhfOnBlur({ target: { name } });
    }
    setValue(name, state.expirationDate, {
      shouldValidate: true,
      shouldTouch: true,
    });
  }, [name, rhfOnBlur, setValue, state.expirationDate]);

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      return;
    }
    setValue(name, state.expirationDate, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });
    if (state.expirationDate.length > 0) {
      const input = document.querySelector(`input[name=${name}]`);
      if (input) {
        input.dispatchEvent(new Event("input", { bubbles: true }));
      }
    }
  }, [state.expirationDate, name, setValue]);

  return (
    <div className={formStyles.inputField}>
      {title && (
        <legend
          className={classnames(formStyles.formTitle, {
            [formStyles.required]: required,
          })}
        >
          {title}
        </legend>
      )}
      <div className={paymentMethodStyles.expirationDateFieldPair}>
        <SelectField
          name={`${name}-m`}
          placeholder="月"
          options={monthOptions()}
          onChange={handleMonthChange}
          onBlur={handleBlur}
          customStyles={customStylesMonth}
          className={formStyles.selectMonth}
          {..._props}
        />
        <SelectField
          name={`${name}-y`}
          placeholder="年"
          options={yearOptions()}
          onChange={handleYearChange}
          onBlur={handleBlur}
          customStyles={customStylesYear}
          {..._props}
        />
      </div>
      <input type="hidden" ref={ref} name={name} value={state.expirationDate} />
      {error && <ErrorMessage msg={error} />}
      {caption && <p className={formStyles.caption}>{caption}</p>}
    </div>
  );
});
ExpirationDateField.displayName = "ExpirationDateField";
export default ExpirationDateField;
